import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { stopFactory } from '@rtls/core/models/convoy/stop.factory';
import { goodFactory } from '@rtls/core/models/goods/good.factory';
import { Result } from '@rtls/core/models/result.model';

import { Pagination } from '@rtls/core/models/rpc/pagination.model';
import { SearchQuery } from '@rtls/core/models/rpc/search-query';

import { RtlsModels } from '@rtls/core/models/rtls/rtls-models';
import { thingFactory } from '@rtls/core/models/thing/thing.factory';
import { journeyPlanFactory } from '@rtls/core/models/transports/journey-plan.factory';
import { journeyFactory } from '@rtls/core/models/transports/journey.factory';
import { transportFactory } from '@rtls/core/models/transports/transport.factory';
import { Factory } from 'factory.ts/lib';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { RtlsExternalService } from '../rtls-external-service.model';

@Injectable()
export class MockedRtlsExternalService<T> implements RtlsExternalService<T> {
	private factory: Factory<any, string | number | symbol>;
	latency = 10;
	errorChancePercentage = 0;
	estimatedDataSetSize = 50;

	constructor() {}

	private generateRandom(uuid: string): T {
		const random = this.factory.build({});
		random.uuid = uuid;
		return random;
	}

	private generatePaginated(pagination?: Pagination): Observable<Result<T>> {
		const defensivePagination = pagination ? pagination : Pagination.default();
		defensivePagination.totalItems = this.estimatedDataSetSize;
		if (defensivePagination.pageSize === -1) {
			defensivePagination.pageSize = this.estimatedDataSetSize;
		}
		return of({
			pagination: defensivePagination,
			data: this.factory.buildList(defensivePagination.pageSize, {}),
		});
	}

	private generateArray(pagination?: Pagination): Observable<T[]> {
		return of(this.factory.buildList(12, {}));
	}

	private throwErrorMaybe<X>(value: X): X {
		if (Math.random() * 100 < this.errorChancePercentage) {
			throw new Error();
		}
		return value;
	}

	// This is ugly but I cannot find a better way to do it without importing it in the prod build yet :(
	private getFactory(model: RtlsModels): Factory<any> {
		switch (model) {
			case RtlsModels.Stop:
				return stopFactory;
			case RtlsModels.Transport:
				return transportFactory;
			case RtlsModels.Journey:
				return journeyFactory;
			case RtlsModels.JourneyPlan:
				return journeyPlanFactory;
			case RtlsModels.Good:
				return goodFactory;
			case RtlsModels.Thing:
				return thingFactory;
		}
		throw new Error(`No Factory created for ${model} yet ! :'(`);
	}

	useModel(models: RtlsModels): RtlsExternalService<T> {
		const service = new MockedRtlsExternalService<T>();
		Object.assign(service, this);
		service.factory = this.getFactory(models);
		return service;
	}

	useBaseUrl(baseUrl: string): void {
		const service = new MockedRtlsExternalService<T>();
		Object.assign(service, this);
	}

	get(uuid: string): Observable<T> {
		return this.wrap(of(this.generateRandom(uuid)));
	}

	getSubRoute(subRoute: string, httpParams?: HttpParams): Observable<any> {
		return this.wrap(of({} as any));
	}

	getByUUIDs(uuids: string[]): Observable<Result<T>> {
		const result = {
			data: uuids.map((uuid) => this.generateRandom(uuid)),
			pagination: Pagination.default(1, uuids.length),
		};
		return this.wrap(of(result));
	}

	getByExternalId(extId: string, provider: string): Observable<T> {
		return this.wrap(of(this.generateRandom(extId)));
	}

	getAll(pagination?: Pagination): Observable<Result<T>> {
		return this.wrap(this.generatePaginated(pagination));
	}

	getAllArray(): Observable<T[]> {
		return this.wrap(this.generateArray());
	}

	getTotalItems(): Observable<number | undefined> {
		return of(this.estimatedDataSetSize);
	}

	search(query: SearchQuery | null, pagination: Pagination, fuzzy: boolean = false): Observable<Result<T>> {
		return this.wrap(this.generatePaginated(pagination));
	}

	prebuiltSearch(searchTerms: string, pagination: Pagination): Observable<Result<T>> {
		return this.wrap(this.generatePaginated(pagination));
	}

	searchWithParams(
		query: SearchQuery | null,
		pagination: Pagination,
		httpParams?: HttpParams,
	): Observable<Result<T>> {
		return this.wrap(this.generatePaginated(pagination));
	}

	// add general behaviour
	wrap<X>(observable: Observable<X>): Observable<X> {
		return observable.pipe(
			delay(this.latency),
			map((x: any) => this.throwErrorMaybe(x)),
		);
	}

	upsert(original: T, updated: T): Observable<T> {
		return this.wrap(of(updated));
	}

	update(original: T, updated: T): Observable<T> {
		return this.wrap(of(updated));
	}

	save(body: T): Observable<T> {
		return this.wrap(of(body));
	}

	delete(uuid: string): Observable<T> {
		return this.wrap(of(this.generateRandom(uuid)));
	}

	deleteAll(): Observable<boolean> {
		return this.wrap(of(true));
	}

	bulkDelete(endpoint: string, uuids: string[]): Observable<T[]> {
		return this.wrap(of([]));
	}

	saveByPut(body: T, uuid?: string) {
		return this.wrap(of(body));
	}

	exec(command: string, uuid: string, payload?: any): Observable<T> {
		return this.wrap(of({} as T));
	}

	downloadResource(
		query: SearchQuery | null,
		pagination: Pagination,
		service: string,
		format: string,
	): Observable<Blob> {
		return this.wrap<Blob>(of(new Blob()));
	}

	getFitlersValues(): Observable<any> {
		return this.wrap(of({} as any));
	}
}
